// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyLang, TyOrganization } from "@/types";
import { isValidSlug, sanitizeSlugInput } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import PhoneInput from "@/UI/inputs/PhoneInput";
import TextInput from "@/UI/inputs/TextInput";
import SlugInfoPopup from "@/UI/popups/SlugInfoPopup";
// --- Types --------------------------------------------------------------------------------------
export type TyOrganizationFormDetails = {
    /** The company being edited, or `null` to create one. */
    organization: TyOrganization | null;
    /**
     * What an enquiry already told us, when this form was opened from one. Only read on a create —
     * it is a starting point for somebody to correct, never a value this form owns.
     */
    prefill?: { name?: string; phone?: string };
    onDone: (organization: TyOrganization) => void;
};

type Props = { onClose: () => void; details?: unknown };
// --- Logic --------------------------------------------------------------------------------------
/**
 * Creating a developer company, and editing one. **Only admins create organizations** — there is no
 * sign-up in this product — so this form is the single door into the system for a new customer.
 *
 * The name is **bilingual content, not a translation**: the company authors each language it publishes
 * in, and it may keep only one. So there is a box per language, Arabic first, and an empty one is
 * dropped rather than stored — a blank English name would otherwise reach its public catalog.
 *
 * The slug is the one field here that is not a detail: it becomes `{slug}.lozicore.com`, the
 * company's public catalog. So it is **cleaned as it is typed** rather than refused on submit, and it
 * carries an info button — "slug" is jargon, and the consequence of changing one is not guessable.
 *
 * `isBlocked` is deliberately absent. Blocking is a consequence worth confirming, so it lives on the
 * row and the record header behind a confirmation rather than as a checkbox in an edit form.
 */
export default function OrganizationFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);
    const { organization, prefill, onDone } = (details ?? { organization: null }) as TyOrganizationFormDetails;
    const isEditing = !!organization;

    /** On a create the Arabic box starts on what an enquiry said the company was called, if one did. */
    const readName = (lang: TyLang) =>
        organization?.name?.find((entry) => entry.lang === lang)?.value ?? (!organization && lang === "ar" ? (prefill?.name ?? "") : "");

    const [names, setNames] = useState<Record<string, string>>({ ar: readName("ar"), en: readName("en") });
    const [slug, setSlug] = useState(organization?.slug ?? "");
    const [city, setCity] = useState(organization?.city ?? "");
    const [address, setAddress] = useState(organization?.address ?? "");
    const [phone, setPhone] = useState<string | undefined>(organization?.phone ?? prefill?.phone ?? "");
    const [email, setEmail] = useState(organization?.email ?? "");
    const [crNumber, setCrNumber] = useState(organization?.crNumber ?? "");
    const [formError, setFormError] = useState<string | null>(null);

    const save = useFetch<{ organization: TyOrganization }>(isEditing ? `/organizations/${organization!._id}` : "/organizations", {}, false);

    useEffect(() => {
        if (!save.succeededAt || !save.data?.organization) return;
        onDone?.(save.data.organization);
        onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [save.succeededAt]);

    /** "Slug" is jargon, and this value is a public address — the popup is where both are explained. */
    const openSlugInfo = () => addComp({ name: "slug-info", Component: SlugInfoPopup, anim: "scale" });

    const handleSubmit = () => {
        setFormError(null);

        const name = constants.languages.map((lang) => ({ lang, value: names[lang]?.trim() ?? "" })).filter((entry) => !!entry.value);
        if (!name.length) return setFormError(t.organizations.nameRequired);

        /**
         * The typed value may still end on the hyphen it is mid-word on, which the API refuses. The
         * field already says what the rule is, so the footer says which field is holding the save up
         * instead of printing the same sentence twice.
         */
        const cleanSlug = slug.trim();
        if (!isValidSlug(cleanSlug)) return setFormError(t.organizations.slugFix);

        const cleanPhone = (phone ?? "").replace(/\s/g, "");

        save.invoke({
            method: isEditing ? "PATCH" : "POST",
            data: {
                name,
                slug: cleanSlug,
                city: city.trim() || null,
                address: address.trim() || null,
                // Untouched, the country picker leaves "+966" alone — which is not a phone number.
                phone: cleanPhone.length > 8 ? cleanPhone : null,
                email: email.trim() || null,
                crNumber: crNumber.trim() || null,
            },
        });
    };

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{isEditing ? t.organizations.editTitle : t.organizations.createTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {constants.languages.map((lang) => (
                    <TextInput
                        key={lang}
                        value={names[lang] ?? ""}
                        onChange={(value) => setNames((previous) => ({ ...previous, [lang]: value }))}
                        label={`${t.organizations.fields.name} · ${constants.languageNames[lang]}`}
                        dir={constants.rtlLanguages.includes(lang as "ar") ? "rtl" : "ltr"}
                        disabled={save.loading}
                    />
                ))}

                <div className="side-form__with-add">
                    {/* Cleaned on the way in, so an Arabic name or an `@` never reaches the box. */}
                    <TextInput
                        value={slug}
                        onChange={(value) => setSlug(sanitizeSlugInput(value))}
                        label={t.organizations.fields.slug}
                        dir="ltr"
                        disabled={save.loading}
                    />
                    <button type="button" className="side-form__info-beside" onClick={openSlugInfo} aria-label={t.slugInfo.title}>
                        <Icon name="info" size={18} />
                    </button>
                </div>
                <p className="side-form__hint">{t.organizations.slugHint.replace("{host}", `${slug || "slug"}.${constants.catalogDomain}`)}</p>
                {/* Only once there is something to be wrong — an empty field is not a mistake yet. */}
                {!!slug && !isValidSlug(slug) && <p className="side-form__error">{t.organizations.slugInvalid}</p>}

                <div className="side-form__row side-form__row--pair">
                    <TextInput value={city} onChange={setCity} label={t.organizations.fields.city} disabled={save.loading} />
                    <TextInput value={crNumber} onChange={setCrNumber} label={t.organizations.fields.crNumber} dir="ltr" disabled={save.loading} />
                </div>

                <PhoneInput value={phone} onChange={setPhone} label={t.organizations.fields.phone} disabled={save.loading} />
                <TextInput value={email} onChange={setEmail} label={t.organizations.fields.email} type="email" dir="ltr" disabled={save.loading} />
                <TextInput value={address} onChange={setAddress} label={t.organizations.fields.address} multiline rows={2} disabled={save.loading} />

                {(formError || save.error) && <p className="side-form__error">{formError ?? save.error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={save.loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={save.loading}>
                    {save.loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
